import { Spacing } from '@persona-id/design-tokens';
import { isEqual, lowerCase, upperFirst } from 'lodash/fp';
import styled from 'styled-components';

import { EXTRACTOR_TYPE_TO_DISPLAY_NAME } from 'dashboard/components/ExtractionPanel/constants';
import { ExtractionCategory } from 'dashboard/components/ExtractionPanel/util';
import { ExtractionType, GuidedPreset } from 'dashboard/models/document';
import {
  DocumentViewerSingletonExtraction,
  FocusedExtraction,
} from 'dashboard/redux/reducers/document-viewer-document-generic';
import ExtractionDetail from 'dashboard/views/DashboardDocumentViewerView/ExtractionDetail';
import ExtractionToggle from 'dashboard/views/DashboardDocumentViewerView/ExtractionToggle';
import { Paragraph } from 'design-system/components/Text/Text';

const Container = styled.div`
  min-width: 300px;
  max-width: 500px;
  background: white;
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const TitleContainer = styled.div`
  padding: ${Spacing[4]} ${Spacing[5]};
`;

const DetailsContainer = styled.div`
  padding: ${Spacing[3]} ${Spacing[5]};
`;

const Footer = styled.div`
  padding: ${Spacing[3]} 0;
  display: flex;
  justify-content: center;
  border-top: ${({ theme }) => `1px ${theme.color.border.secondary} solid`};
`;

interface ExtractionHoverCardProps {
  extraction: DocumentViewerSingletonExtraction;
  focusedExtractions: FocusedExtraction[];
  setFocusedExtractions: (focusedExtractions: FocusedExtraction[]) => void;
}

export const ExtractionHoverCard = ({
  extraction,
  focusedExtractions,
  setFocusedExtractions,
}: ExtractionHoverCardProps) => {
  const extractionGroup = extraction.extractionGroup;
  const {
    fieldName,
    fieldIdentifier,
    extractionType,
    extractorType,
    sourceValue,
    matchLevelMinimum,
    guidedPreset,
  } = extractionGroup;

  const focusedExtractionMatch = focusedExtractions.find((focusedExtraction) =>
    isEqual(focusedExtraction.extractionGroup, extractionGroup),
  );
  const extractionGroupIsFocused = focusedExtractionMatch != null;

  const handleToggleDown = () => {
    setFocusedExtractions([
      {
        extractionGroup: extractionGroup,
        focusedBoundingBoxId: extractionGroupIsFocused
          ? (focusedExtractionMatch.focusedBoundingBoxId + 1) % extractionGroup.numResults
          : 1,
      },
    ]);
  };

  const handleToggleUp = () => {
    setFocusedExtractions([
      {
        extractionGroup: extractionGroup,
        focusedBoundingBoxId: extractionGroupIsFocused
          ? (focusedExtractionMatch.focusedBoundingBoxId - 1 + extractionGroup.numResults) %
            extractionGroup.numResults
          : extractionGroup.numResults - 1,
      },
    ]);
  };

  let comparisonSource = undefined;
  switch (guidedPreset) {
    case GuidedPreset.AddressLoose:
    case GuidedPreset.AddressStrict:
      comparisonSource = ExtractionCategory.Address;
      break;
    case GuidedPreset.NameLoose:
    case GuidedPreset.NameStrict:
      comparisonSource = ExtractionCategory.Name;
      break;
    case GuidedPreset.GenericLoose:
    case GuidedPreset.GenericLooseAi:
    case GuidedPreset.GenericStrict:
      comparisonSource = fieldIdentifier;
      break;
  }

  return (
    <Container>
      <TitleContainer>
        <Paragraph size='lg' color='primary' weight='bold'>
          {upperFirst(lowerCase(fieldName))}
        </Paragraph>
        {extractorType && (
          <Paragraph size='sm'>{EXTRACTOR_TYPE_TO_DISPLAY_NAME[extractorType]}</Paragraph>
        )}
        {matchLevelMinimum && (
          <Paragraph size='sm'>Match requirement: {upperFirst(matchLevelMinimum)}</Paragraph>
        )}
      </TitleContainer>
      {extractionType === ExtractionType.Guided && (
        <DetailsContainer>
          <Paragraph>
            Comparison source:{' '}
            {comparisonSource ? upperFirst(lowerCase(comparisonSource)) : 'Custom'}
          </Paragraph>
          <Paragraph color='primary'>{sourceValue || 'None'}</Paragraph>
        </DetailsContainer>
      )}
      <ExtractionDetail
        key={`${extractionGroup.fieldName}-${extractionGroup.fieldIdentifier}-${extractionGroup.extractionValue}`}
        extractionGroup={extractionGroup}
      />
      <Footer>
        <ExtractionToggle
          index={extraction.result.id + 1}
          numTotal={extractionGroup.numResults}
          handleToggleDown={handleToggleDown}
          handleToggleUp={handleToggleUp}
        />
      </Footer>
    </Container>
  );
};

export default ExtractionHoverCard;
